import moment from "moment";
import strings from "global/constants/StringConstants";
import urls from "global/constants/UrlConstants";
import { fillCopy } from "helpers/methods";
import { hasAccessTo, isAdmin } from "layouts/Config";

/**
 * Seasonal promotion phases, in the order they run. `none` is every day
 * outside the schedule below.
 */
export type PromoPhase = "teaser" | "bf" | "bfFinal" | "ye" | "yeFinal" | "none";
/** The two sales a phase belongs to. The teaser trails Black Friday. */
export type PromoCampaign = "bf" | "ye";

const PHASES: PromoPhase[] = ["teaser", "bf", "bfFinal", "ye", "yeFinal", "none"];

/**
 * The 2026 campaign calendar. There is no promotions API yet, so the phase is
 * read off the date here — every window is inclusive and in UTC, the same as
 * the backend's sale windows (`promotionSchedule`), so the banner, popup and
 * strip change at the same moment the sale price does, wherever the viewer
 * is. Kept in step with the marketing site's sale schedule
 * (ss-inkfree-website `signature-savings-sale/_data/schedule.ts`): "final
 * days" is the last two days of each offer there too. Move a date here and
 * the strip/popup copy follows it.
 */
const liveSchedule = {
  teaserStart: "2026-11-01",
  bfStart: "2026-11-16",
  bfFinalStart: "2026-12-13",
  bfEnd: "2026-12-14",
  yeStart: "2026-12-15",
  yeFinalStart: "2026-12-30",
  yeEnd: "2026-12-31",
};

/**
 * UAT rehearsal: every phase squeezed into one week so the whole campaign can
 * be checked before it goes live. Picked by `REACT_APP_ENV=uat`, which only
 * the UAT build sets (`buildspec_uat.yml`); production and local runs keep
 * the live dates. UAT's backend `promotionSchedule` has to use these same
 * dates for the sale prices to line up.
 */
const uatSchedule: typeof liveSchedule = {
  teaserStart: "2026-10-29",
  bfStart: "2026-10-30",
  bfFinalStart: "2026-11-03",
  bfEnd: "2026-11-03",
  yeStart: "2026-11-04",
  yeFinalStart: "2026-11-05",
  yeEnd: "2026-11-05",
};

export const promoSchedule =
  process.env.REACT_APP_ENV === "uat" ? uatSchedule : liveSchedule;

/** Discount per campaign: first year on annual, first 3 months on monthly. */
export const promoDiscounts: Record<PromoCampaign, { annual: number; monthly: number }> = {
  bf: { annual: 70, monthly: 60 },
  ye: { annual: 60, monthly: 50 },
};

const day = (iso: string) => moment.utc(iso, "YYYY-MM-DD");

const phaseForDate = (now: moment.Moment): PromoPhase => {
  const s = promoSchedule;
  const within = (from: string, to: string) =>
    now.isBetween(day(from).startOf("day"), day(to).endOf("day"), undefined, "[]");
  if (within(s.teaserStart, day(s.bfStart).subtract(1, "day").format("YYYY-MM-DD"))) return "teaser";
  if (within(s.bfStart, day(s.bfFinalStart).subtract(1, "day").format("YYYY-MM-DD"))) return "bf";
  if (within(s.bfFinalStart, s.bfEnd)) return "bfFinal";
  if (within(s.yeStart, day(s.yeFinalStart).subtract(1, "day").format("YYYY-MM-DD"))) return "ye";
  if (within(s.yeFinalStart, s.yeEnd)) return "yeFinal";
  return "none";
};

/**
 * Current phase. Outside production a `?promoPhase=<phase>` query param
 * overrides the calendar so each phase can be checked before its dates.
 */
export const getPromoPhase = (
  search: string,
  now = moment.utc(),
): PromoPhase => {
  if (!urls.PROD) {
    const override = new URLSearchParams(search).get(
      strings.promotions.phaseOverrideParam,
    );
    if (override && PHASES.includes(override as PromoPhase)) {
      return override as PromoPhase;
    }
  }
  return phaseForDate(now);
};

/**
 * Outside production, `?promoPrices=1` prices the campaign into the plan cards
 * and checkout note even while the backend's offer switch is off — for
 * reviewing the design only; nothing it shows will be charged.
 */
export const isPromoPricePreview = (search: string): boolean =>
  !urls.PROD &&
  new URLSearchParams(search).get(strings.promotions.plan.pricePreviewParam) ===
    "1";

/**
 * The `?promoPhase=` / `?promoPrices=` overrides, kept when the promo hands off to My Plan so a
 * forced phase survives the navigation. Empty in production.
 */
export const promoOverrideSearch = (search: string): string => {
  if (urls.PROD) return "";
  const current = new URLSearchParams(search);
  const kept = new URLSearchParams();
  [
    strings.promotions.phaseOverrideParam,
    strings.promotions.plan.pricePreviewParam,
  ].forEach((param) => {
    const value = current.get(param);
    if (value) kept.set(param, value);
  });
  const query = kept.toString();
  return query ? `?${query}` : "";
};

export const campaignOf = (phase: PromoPhase): PromoCampaign | null => {
  if (phase === "none") return null;
  return phase === "ye" || phase === "yeFinal" ? "ye" : "bf";
};

export const isFinalDays = (phase: PromoPhase) =>
  phase === "bfFinal" || phase === "yeFinal";

/**
 * Only someone who can actually buy sees a promotion — an account admin with
 * the billing add permission — since every offer leads straight to checkout.
 */
export const canClaimPromo = () =>
  isAdmin() && hasAccessTo(strings.resources.billing, strings.addPermission);

/** Whole-percent discount a campaign gives on one billing cycle. */
export const promoPercentFor = (campaign: PromoCampaign, isAnnual: boolean) =>
  isAnnual ? promoDiscounts[campaign].annual : promoDiscounts[campaign].monthly;

/**
 * Opens the Mailzzy signup popup behind the teaser's "Notify me". The Mailzzy
 * loader draws its popup as soon as it runs, and marks itself as loaded with a
 * window flag, so a later click clears that flag and runs it again. Mailzzy
 * itself stays quiet for a visitor who has already signed up through it.
 */
export const openPromoNotifyPopup = () => {
  // Already on screen.
  if (document.getElementById("mz-popup-frame")) return;
  const src = urls.PROMO_NOTIFY_POPUP_SCRIPT;
  const popupId = (src.split("/").pop() ?? "").replace(/\.js$/, "");
  delete (window as any)[`__mzPopup_${popupId}`];
  document
    .querySelectorAll(`script[src="${src}"]`)
    .forEach((script) => script.remove());
  const script = document.createElement("script");
  script.src = src;
  script.async = true;
  document.body.appendChild(script);
};

/** Values every promo copy template draws from. */
export const promoCopyValues = (): Record<string, string | number> => ({
  bfStart: day(promoSchedule.bfStart).format("MMMM D"),
  bfEnd: day(promoSchedule.bfEnd).format("MMMM D"),
  yeEnd: day(promoSchedule.yeEnd).format("MMMM D"),
  bfAnnual: promoDiscounts.bf.annual,
  bfMonthly: promoDiscounts.bf.monthly,
  yeAnnual: promoDiscounts.ye.annual,
  yeMonthly: promoDiscounts.ye.monthly,
});

export const promoEndShort = (campaign: PromoCampaign) =>
  day(campaign === "bf" ? promoSchedule.bfEnd : promoSchedule.yeEnd)
    .format("MMM D")
    .toUpperCase();

export interface PromoTextRun {
  text: string;
  bold: boolean;
}

/** Splits a filled `**bold**` template into plain and emphasised runs. */
export const toTextRuns = (template: string): PromoTextRun[] =>
  fillCopy(template, promoCopyValues())
    .split("**")
    // Odd segments sit between a pair of markers. Parity is taken before
    // empty segments are dropped, so a template opening on `**` still works.
    .map((text, index) => ({ text, bold: index % 2 === 1 }))
    .filter((run) => run.text.length > 0);

/*
  Dismissals are a per-viewer convenience: the strip stays closed for the rest
  of its phase, the popup for the rest of its campaign. Keyed by the signed-in
  email so a shared browser doesn't carry one user's choice to the next.
  Storage can be missing or throw (private mode, blocked site data), in which
  case the promo simply shows again next visit.
*/
const storageKey = (kind: "strip" | "popup", scope: string, email: string) =>
  `${strings.promotions.storagePrefix}.${kind}.${scope}.${email.toLowerCase()}`;

export const isPromoDismissed = (
  kind: "strip" | "popup",
  scope: string,
  email: string,
): boolean => {
  try {
    return window.localStorage.getItem(storageKey(kind, scope, email)) === "1";
  } catch {
    return false;
  }
};

export const dismissPromo = (
  kind: "strip" | "popup",
  scope: string,
  email: string,
) => {
  try {
    window.localStorage.setItem(storageKey(kind, scope, email), "1");
  } catch {
    // Storage unavailable — the dismissal lasts for this page view only.
  }
};
